/**
 * React-Query hooks for bullet-mapping configuration (PR2 #10).
 *
 * - useCustomFieldDefs: list of JTL custom-field definitions (dropdown options)
 * - useBulletMappings: full bullet_mapping for an integration (all channels)
 * - useSaveBulletMapping: upsert per-channel mapping with cache invalidation + toast
 *
 * All hooks are workspace-scoped via the integrationId, which the backend
 * validates against g.workspace_id (SG-1 Workspace-Isolation).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  getBulletMappings,
  getCustomFieldDefs,
  saveBulletMapping,
  type BulletMapping,
} from '@/lib/api/integrations-bullet-mapping'

export function useCustomFieldDefs(integrationId: string | null) {
  return useQuery({
    queryKey: ['jtl-customfields', integrationId],
    queryFn: () => getCustomFieldDefs(integrationId as string),
    enabled: !!integrationId,
    staleTime: 60_000,
  })
}

export function useBulletMappings(integrationId: string | null) {
  return useQuery({
    queryKey: ['bullet-mapping', integrationId],
    queryFn: () => getBulletMappings(integrationId as string),
    enabled: !!integrationId,
    staleTime: 30_000,
  })
}

export function useSaveBulletMapping(integrationId: string) {
  const qc = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (input: { sales_channel_id: string; mapping: BulletMapping }) =>
      saveBulletMapping(integrationId, input.sales_channel_id, input.mapping),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bullet-mapping', integrationId] })
      toast.success(t('remakeApply.mapping.savedToast'))
    },
  })
}
